import React, { useState } from "react";
import {
  Table,
  Button,
  Group,
  Text,
  Badge,
  ActionIcon,
  TextInput,
  Select,
  Stack,
  Card,
  Title,
} from "@mantine/core";
import {
  IconPlus,
  IconEye,
  IconPlayerPlay,
  IconCheck,
  IconEdit,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useMeetings, useStartMeeting, useFinishMeeting } from "../hooks";
import { RoleGuard } from "../components/RoleGuard";

export const MeetingList: React.FC = () => {
  const navigate = useNavigate();
  const { data: meetings, isLoading } = useMeetings();
  const startMeeting = useStartMeeting();
  const finishMeeting = useFinishMeeting();

  console.log("MeetingList - meetings:", meetings);
  console.log("MeetingList - isLoading:", isLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredMeetings =
    meetings?.filter((meeting) => {
      const matchesSearch =
        meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meeting.meetingNumber.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || meeting.status === statusFilter;
      return matchesSearch && matchesStatus;
    }) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "gray";
      case "SCHEDULED":
        return "blue";
      case "IN_PROGRESS":
        return "yellow";
      case "COMPLETED":
        return "green";
      case "CANCELLED":
        return "red";
      default:
        return "gray";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleStartMeeting = (id: string) => {
    startMeeting.mutate({ id });
  };

  const handleFinishMeeting = (id: string) => {
    finishMeeting.mutate({ id });
  };

  if (isLoading) {
    return <div>Loading meetings...</div>;
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Bid Committee Meetings</Title>
        <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate("/bid-committee/meetings/new")}
          >
            New Meeting
          </Button>
        </RoleGuard>
      </Group>

      <Card>
        <Group gap="md" mb="md">
          <TextInput
            placeholder="Search meetings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || "all")}
            data={[
              { value: "all", label: "All Status" },
              { value: "SCHEDULED", label: "Scheduled" },
              { value: "IN_PROGRESS", label: "In Progress" },
              { value: "COMPLETED", label: "Completed" },
              { value: "CANCELLED", label: "Cancelled" },
            ]}
            style={{ width: 200 }}
          />
        </Group>

        <Table>
          <thead>
            <tr>
              <th>Meeting Number</th>
              <th>Title</th>
              <th>Location</th>
              <th>Planned Start</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMeetings.map((meeting) => (
              <tr key={meeting.id}>
                <td>
                  <Text fw={500}>{meeting.meetingNumber}</Text>
                </td>
                <td>{meeting.title}</td>
                <td>{meeting.location}</td>
                <td>{formatDate(meeting.plannedStart)}</td>
                <td>
                  <Badge color={getStatusColor(meeting.status)}>
                    {meeting.status.replace("_", " ")}
                  </Badge>
                </td>
                <td>
                  <Group gap="xs">
                    <ActionIcon
                      color="blue"
                      onClick={() =>
                        navigate(`/bid-committee/meetings/${meeting.id}`)
                      }
                    >
                      <IconEye size={16} />
                    </ActionIcon>

                    {meeting.status === "DRAFT" && (
                      <RoleGuard
                        allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}
                      >
                        <ActionIcon
                          color="orange"
                          onClick={() =>
                            navigate(`/bid-committee/meetings/${meeting.id}/edit`)
                          }
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </RoleGuard>
                    )}

                    {meeting.status === "SCHEDULED" && (
                      <RoleGuard
                        allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}
                      >
                        <Group gap="xs">
                          <ActionIcon
                            color="orange"
                            onClick={() =>
                              navigate(`/bid-committee/meetings/${meeting.id}/edit`)
                            }
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            color="yellow"
                            onClick={() => handleStartMeeting(meeting.id)}
                            loading={startMeeting.isPending}
                          >
                            <IconPlayerPlay size={16} />
                          </ActionIcon>
                        </Group>
                      </RoleGuard>
                    )}

                    {meeting.status === "IN_PROGRESS" && (
                      <RoleGuard
                        allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}
                      >
                        <ActionIcon
                          color="green"
                          onClick={() => handleFinishMeeting(meeting.id)}
                          loading={finishMeeting.isPending}
                        >
                          <IconCheck size={16} />
                        </ActionIcon>
                      </RoleGuard>
                    )}
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Stack>
  );
};
